import Button from '@mui/material/Button';
import Fade from '@mui/material/Fade';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { SaveBarContainer, useDisclosure } from '@tyro/core';
import { useTranslation } from '@tyro/i18n';
import { AddHighlightModal } from './add-highlight-modal';
import type { TimelineItemProps } from './item';
import { SelectedEventsForHighlightsModal } from './selected-events-for-highlight';

type SaveHighlightBarProps = {
  isAdding: boolean;
  events: TimelineItemProps['event'][];
  onRemoveEvent: (eventId: TimelineItemProps['event']['id']) => void;
  onClose: () => void;
};

export function SaveHighlightBar({
  isAdding,
  events,
  onRemoveEvent,
  onClose,
}: SaveHighlightBarProps) {
  const { t } = useTranslation(['common', 'people']);
  const {
    isOpen: showEvents,
    onOpen: openShowEvents,
    onClose: closeShowEvents,
  } = useDisclosure();

  const {
    isOpen: isOpenHighlightForm,
    onOpen: openHighlightForm,
    onClose: closeHighlightForm,
  } = useDisclosure();

  const hasEvents = events.length > 0;

  return (
    <>
      <SaveBarContainer
        slideProps={{ in: isAdding }}
        containerProps={{
          justifyContent: 'flex-end',
        }}
        contentProps={{
          flex: {
            xs: 1,
            sm: 'inherit',
          },
        }}
      >
        <Stack
          direction="row"
          justifyContent="flex-end"
          alignItems="center"
          gap={2}
          px={3.25}
          py={2}
        >
          <Typography variant="subtitle2" component="span">
            {t('people:createHighlightSaveBar')}
          </Typography>
          <Fade in={hasEvents}>
            <Link
              component="button"
              variant="subtitle2"
              onClick={openShowEvents}
            >
              {t('people:eventSelectedForHighlight', {
                count: events.length,
                defaultValue: '0',
              })}
            </Link>
          </Fade>
          <Button variant="soft" color="inherit" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Tooltip
            title={hasEvents ? undefined : t('people:pleaseSelectEvents')}
          >
            <span>
              <Button
                variant="contained"
                onClick={openHighlightForm}
                disabled={!hasEvents}
              >
                {t('common:actions.next')}
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </SaveBarContainer>
      <SelectedEventsForHighlightsModal
        open={showEvents}
        events={events}
        onClose={closeShowEvents}
        onRemoveEvent={onRemoveEvent}
      />
      <AddHighlightModal
        open={isOpenHighlightForm}
        events={events}
        onClose={closeHighlightForm}
        onSuccess={onClose}
      />
    </>
  );
}
